import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Tag } from "lucide-react";
import apiClient from "@/api/client";
import { toastHelpers } from "@/lib/toast-helpers";
import { CategoryForm } from "@/components/forms/CategoryForm";
import { AdminCategoriesTable } from "@/components/admin/AdminCategoriesTable";
import { PaginationControlsComponent } from "@/components/ui/pagination-controls";
import { usePagination } from "@/hooks/usePagination";
import { InlineLoading } from "@/components/ui/loading-spinner";
import type { Category } from "@/types";
import { useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

export function AdminMenuManagement() {
  const { t } = useTranslation();
  const router = useRouter();

  useEffect(() => {
    const decodedToken = apiClient.isAuthenticated();

    if (!decodedToken) {
      toastHelpers.sessionExpired();
      router.navigate({ to: "/login" });
    }
  }, []);

  const [categorySearch, setCategorySearch] = useState("");
  const [debouncedCategorySearch, setDebouncedCategorySearch] = useState("");
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [showCreateCategoryForm, setShowCreateCategoryForm] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const queryClient = useQueryClient();

  // Responsive breakpoint detection
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Pagination hook
  const categoriesPagination = usePagination({
    initialPage: 1,
    initialPageSize: 10,
    total: 0,
  });

  // Debounce category search
  useEffect(() => {
    if (categorySearch !== debouncedCategorySearch) {
      setIsSearching(true);
    }
    const timer = setTimeout(() => {
      setDebouncedCategorySearch(categorySearch);
      categoriesPagination.goToFirstPage();
      setIsSearching(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [categorySearch, debouncedCategorySearch]);

  // Fetch categories with pagination
  const { data: categoriesData, isLoading: isLoadingCategories } = useQuery({
    queryKey: [
      "admin-categories",
      categoriesPagination.page,
      categoriesPagination.pageSize,
      debouncedCategorySearch,
    ],
    queryFn: () =>
      apiClient
        .getCategories({
          page: categoriesPagination.page,
          per_page: categoriesPagination.pageSize,
          search: debouncedCategorySearch || undefined,
        })
        .then((res: any) => res.data),
  });

  // Show loading screen when backend is called
  const isLoadingAny = isLoadingCategories;

  // Extract data and pagination info
  const categories = Array.isArray(categoriesData)
    ? categoriesData
    : (categoriesData as any)?.data || [];
  const categoriesPaginationInfo = (categoriesData as any)?.pagination || {
    total: 0,
  };

  // Delete category mutation
  const deleteCategoryMutation = useMutation({
    mutationFn: (id: string) => apiClient.deleteCategory(id),
    onSuccess: (_) => {
      queryClient.invalidateQueries({ queryKey: ["admin-categories"] });
      toastHelpers.success(t("Toast.Category.DeleteSuccess"));
    },
    onError: () => {
      toastHelpers.error(t("Toast.Category.DeleteError"));
    },
  });

  const handleFormSuccess = () => {
    setShowCreateCategoryForm(false);
    setEditingCategory(null);
  };

  const handleCancelForm = () => {
    setShowCreateCategoryForm(false);
    setEditingCategory(null);
  };

  const handleDeleteCategory = (category: Category) => {
    if (
      confirm(
        t("AdminMenuManagement.Confirm.DeleteCategory", { name: category.name })
      )
    ) {
      deleteCategoryMutation.mutate(category.id.toString());
    }
  };

  return (
    <div
      className={`${isMobile ? "p-4" : "p-6"} space-y-${isMobile ? "4" : "6"}`}
    >
      {/* Search and Add Button */}
      <Card>
        <CardContent className={isMobile ? "pt-4 pb-4" : "pt-6"}>
          <div
            className={`${isMobile ? "space-y-3" : "flex items-center justify-between gap-4"}`}
          >
            <div className="relative flex-1">
              <Search
                className={`absolute left-2 top-2.5 text-muted-foreground ${isMobile ? "h-3 w-3" : "h-4 w-4"}`}
              />
              <Input
                placeholder={
                  isMobile
                    ? t("AdminMenuManagement.SearchPlaceholderMobile")
                    : t("AdminMenuManagement.SearchPlaceholder")
                }
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
                className={isMobile ? "pl-8 text-sm" : "pl-8"}
              />
              {isSearching && (
                <div className="absolute right-2 top-2.5">
                  <InlineLoading size="sm" />
                </div>
              )}
            </div>
            <Button
              onClick={() => setShowCreateCategoryForm(true)}
              className={`gap-2 ${isMobile ? "w-full text-sm" : ""}`}
              size={isMobile ? "sm" : "default"}
            >
              <Plus className={isMobile ? "h-3 w-3" : "h-4 w-4"} />
              {t("AdminMenuManagement.AddCategory")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Content area with skeletons when loading */}
      {isLoadingAny ? (
        <div className="space-y-4">
          {/* Categories skeleton */}
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 bg-muted/60 rounded animate-pulse" />
                    <div className="space-y-2">
                      <div className="h-5 w-32 bg-muted/60 rounded animate-pulse" />
                      <div className="h-4 w-48 bg-muted/40 rounded animate-pulse" />
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <div className="h-8 w-8 bg-muted/40 rounded animate-pulse" />
                    <div className="h-8 w-8 bg-muted/40 rounded animate-pulse" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Pagination skeleton */}
          <div className="flex justify-center">
            <div className="flex items-center space-x-2">
              <div className="h-8 w-16 bg-muted/40 rounded animate-pulse" />
              <div className="h-8 w-8 bg-muted/50 rounded animate-pulse" />
              <div className="h-8 w-8 bg-muted/50 rounded animate-pulse" />
              <div className="h-8 w-8 bg-muted/40 rounded animate-pulse" />
              <div className="h-8 w-16 bg-muted/40 rounded animate-pulse" />
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Categories List */}
          {categories.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-8">
                  <Tag className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    {t("AdminMenuManagement.EmptyState.Title")}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {categorySearch
                      ? t("AdminMenuManagement.EmptyState.NoMatch")
                      : t("AdminMenuManagement.EmptyState.GetStarted")}
                  </p>
                  {!categorySearch && (
                    <div className="mt-6">
                      <Button
                        onClick={() => setShowCreateCategoryForm(true)}
                        className="gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        {t("AdminMenuManagement.AddCategory")}
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <AdminCategoriesTable
              data={categories}
              onEdit={setEditingCategory}
              onDelete={handleDeleteCategory}
              isLoading={isLoadingCategories}
            />
          )}

          {/* Categories Pagination */}
          {categories.length > 0 && (
            <div className="mt-6 space-y-4">
              {isLoadingAny && (
                <div className="flex justify-center">
                  <InlineLoading
                    text={t("AdminMenuManagement.Loading.Categories")}
                  />
                </div>
              )}
              <PaginationControlsComponent
                pagination={categoriesPagination}
                total={categoriesPaginationInfo.total || categories.length}
              />
            </div>
          )}
        </div>
      )}

      {/* Category Form Modal */}
      {(showCreateCategoryForm || editingCategory) && (
        <div
          className="fixed top-0 left-0 w-screen h-screen bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4"
          style={{
            backdropFilter: "blur(4px)",
            margin: 0,
            padding: 0,
            minHeight: "100vh",
            minWidth: "100vw",
          }}
        >
          <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto scrollbar-hide p-4 sm:p-0">
            <CategoryForm
              category={editingCategory || undefined}
              mode={editingCategory ? "edit" : "create"}
              onSuccess={handleFormSuccess}
              onCancel={handleCancelForm}
            />
          </div>
        </div>
      )}
    </div>
  );
}
